import { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, useTheme, Paper, Button, Chip, CircularProgress, Alert, Divider,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Replay as ReplayIcon,
  Delete as DeleteIcon,
  Share as ShareIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import {
  ResponsiveContainer, ComposedChart, Line, Area, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, Legend, ReferenceLine,
} from 'recharts';
import { tokens } from '../../theme';
import { getAccuracyReport, rerunAccuracyDiff, deleteAccuracyReport, setAccuracyReportShared } from '../../api/runningApi';
import { apiErrorMessage } from '../../utils/apiError';

const BAND_LABEL = {
  steep_down: 'Steep down (< −12%)',
  down: 'Down (−12…−3%)',
  flat: 'Flat (−3…3%)',
  up: 'Up (3…12%)',
  steep_up: 'Steep up (> 12%)',
};

// ── formatters ────────────────────────────────────────────────────────────────
const fmtSignedSecs = (s) => {
  if (s == null) return '—';
  const sign = s < 0 ? '-' : '+';
  const a = Math.abs(s);
  const h = Math.floor(a / 3600);
  const m = Math.floor((a % 3600) / 60);
  const sec = Math.round(a % 60);
  if (h > 0) return `${sign}${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  return `${sign}${m}:${String(sec).padStart(2, '0')}`;
};
const fmtSecs = (s) => {
  if (s == null) return '—';
  const a = Math.abs(s);
  const h = Math.floor(a / 3600);
  const m = Math.floor((a % 3600) / 60);
  const sec = Math.round(a % 60);
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}` : `${m}:${String(sec).padStart(2, '0')}`;
};
const fmtPace = (p) => {
  if (p == null) return '—';
  const m = Math.floor(p);
  const s = Math.round((p - m) * 60);
  return `${m}:${String(s).padStart(2, '0')}`;
};
const fmtSignedPace = (p) => {
  if (p == null) return '—';
  const sign = p < 0 ? '-' : '+';
  const a = Math.abs(p);
  const m = Math.floor(a);
  const s = Math.round((a - m) * 60);
  return `${sign}${m}:${String(s).padStart(2, '0')}`;
};

export default function RunningPredictionAccuracy() {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rerunning, setRerunning] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setLoading(true);
    getAccuracyReport(reportId)
      .then(setReport)
      .catch((err) => setError(apiErrorMessage(err, 'Failed to load report')))
      .finally(() => setLoading(false));
  }, [reportId]);

  const data = report?.report_data || {};
  const overall = data.overall || {};
  const meta = data.meta || {};
  const rerun = report?.rerun_diff || null;

  const errorMap = useMemo(() => (data.error_map || []).map((r) => ({
    ...r,
    elapsed_error_min: r.elapsed_error_s != null ? r.elapsed_error_s / 60 : null,
  })), [data.error_map]);

  const handleRerun = async () => {
    setRerunning(true);
    setError('');
    try {
      const updated = await rerunAccuracyDiff(reportId);
      setReport(updated);
    } catch (err) {
      setError(apiErrorMessage(err, 'Re-run failed'));
    } finally {
      setRerunning(false);
    }
  };

  const shareUrl = `${window.location.origin}/running/prediction-accuracy/${reportId}`;

  const copyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt('Copy this shareable link:', shareUrl);
    }
  };

  // Owner: make the report public (if needed) and copy its link.
  const handleShare = async () => {
    setSharing(true);
    setError('');
    try {
      if (!report?.is_public) {
        const res = await setAccuracyReportShared(reportId, true);
        setReport((r) => ({ ...r, is_public: res.is_public }));
      }
      await copyShareLink();
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to share report'));
    } finally {
      setSharing(false);
    }
  };

  const handleUnshare = async () => {
    setSharing(true);
    setError('');
    try {
      const res = await setAccuracyReportShared(reportId, false);
      setReport((r) => ({ ...r, is_public: res.is_public }));
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to update sharing'));
    } finally {
      setSharing(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this accuracy report?')) return;
    try {
      await deleteAccuracyReport(reportId);
      navigate('/running/prediction-accuracy');
    } catch (err) {
      setError(apiErrorMessage(err, 'Delete failed'));
    }
  };

  if (loading) {
    return <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>;
  }
  if (error && !report) {
    return <Box p={3}><Alert severity="error">{error}</Alert></Box>;
  }

  const matchColor = { good: 'success', warn: 'warning', different: 'error' }[meta.course_match] || 'default';
  const totalErr = overall.total_time_error_s;
  const errIsFast = totalErr != null && totalErr < 0; // predicted faster than reality

  return (
    <Box p={{ xs: 1.5, md: 3 }}>
      {/* Header */}
      <Box display="flex" alignItems="center" gap={1.5} mb={2} flexWrap="wrap">
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} size="small">
          Back
        </Button>
        <Typography variant="h5" fontWeight={600}>Prediction Accuracy</Typography>
        <Chip label={`Course match: ${meta.course_match || '—'}`} color={matchColor} size="small" />
        {report.is_public && (
          <Chip icon={<LinkIcon sx={{ fontSize: 14 }} />} label="Public link" color="info" size="small" variant="outlined" />
        )}
        <Box flexGrow={1} />
        {report.is_owner && (
          <>
            <Tooltip title={report.is_public ? 'Copy shareable link' : 'Make this report shareable and copy the link'}>
              <Button
                variant="outlined" size="small"
                startIcon={sharing ? <CircularProgress size={14} color="inherit" /> : <ShareIcon />}
                onClick={handleShare} disabled={sharing}
              >
                {copied ? 'Link copied!' : report.is_public ? 'Copy link' : 'Share'}
              </Button>
            </Tooltip>
            {report.is_public && (
              <Tooltip title="Stop sharing (make private)">
                <Button size="small" color="inherit" onClick={handleUnshare} disabled={sharing}>Unshare</Button>
              </Tooltip>
            )}
            <Button
              variant="contained" size="small" startIcon={rerunning ? <CircularProgress size={14} color="inherit" /> : <ReplayIcon />}
              onClick={handleRerun} disabled={rerunning}
              sx={{ backgroundColor: colors.greenAccent[700], '&:hover': { backgroundColor: colors.greenAccent[600] } }}
            >
              {rerunning ? 'Re-running…' : 'Re-run algorithm & diff'}
            </Button>
            <Tooltip title="Delete report">
              <Button color="error" size="small" onClick={handleDelete}><DeleteIcon fontSize="small" /></Button>
            </Tooltip>
          </>
        )}
        {!report.is_owner && (
          <Tooltip title="Copy link to this report">
            <Button variant="outlined" size="small" startIcon={<LinkIcon />} onClick={copyShareLink}>
              {copied ? 'Link copied!' : 'Copy link'}
            </Button>
          </Tooltip>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Typography variant="body2" color="text.secondary" mb={2}>
        <strong>{report.label_prediction}</strong> (prediction) vs <strong>{report.label_actual}</strong> (actual) ·{' '}
        {overall.race_distance_km != null ? `${overall.race_distance_km} km` : ''}
      </Typography>

      {/* Headline error banner */}
      <Paper sx={{ p: 2, mb: 2, backgroundColor: colors.primary[800] }}>
        <Box display="flex" gap={4} flexWrap="wrap" alignItems="center">
          <Box>
            <Typography variant="caption" color="text.secondary">Predicted finish</Typography>
            <Typography variant="h6">{fmtSecs(overall.predicted_total_time_s)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Actual finish</Typography>
            <Typography variant="h6">{fmtSecs(overall.actual_total_time_s)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Error</Typography>
            <Typography variant="h6" color={errIsFast ? colors.redAccent[400] : colors.greenAccent[400]}>
              {fmtSignedSecs(totalErr)} {overall.total_time_error_pct != null ? `(${overall.total_time_error_pct}%)` : ''}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {errIsFast ? 'prediction was too fast' : totalErr > 0 ? 'prediction was too slow' : ''}
            </Typography>
          </Box>
          {meta.worst_checkpoint_segment && (
            <Box>
              <Typography variant="caption" color="text.secondary">Worst segment</Typography>
              <Typography variant="body1">
                {meta.worst_checkpoint_segment.name} @ {meta.worst_checkpoint_segment.distance_km} km
              </Typography>
              <Typography variant="caption" color={colors.redAccent[300]}>
                {fmtSignedSecs(meta.worst_checkpoint_segment.segment_error_s)}
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Overall totals */}
      <SectionTitle>Overall totals</SectionTitle>
      <TableContainer component={Paper} sx={{ mb: 3, backgroundColor: colors.primary[800] }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Metric</TableCell>
              <TableCell align="right">Predicted</TableCell>
              <TableCell align="right">Actual</TableCell>
              <TableCell align="right">Error</TableCell>
              <TableCell align="right">%</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(overall.rows || []).map((r) => {
              const isPace = r.unit === 'min/km';
              return (
                <TableRow key={r.metric}>
                  <TableCell>{r.metric}</TableCell>
                  <TableCell align="right">{isPace ? fmtPace(r.predicted) : fmtSecs(r.predicted)}</TableCell>
                  <TableCell align="right">{isPace ? fmtPace(r.actual) : fmtSecs(r.actual)}</TableCell>
                  <TableCell align="right" sx={{ color: r.error < 0 ? colors.redAccent[300] : colors.greenAccent[300] }}>
                    {isPace ? fmtSignedPace(r.error) : fmtSignedSecs(r.error)}
                  </TableCell>
                  <TableCell align="right">{r.error_pct != null ? `${r.error_pct}%` : '—'}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Error map chart */}
      <SectionTitle>Where the prediction drifted</SectionTitle>
      <Paper sx={{ p: 2, mb: 3, backgroundColor: colors.primary[800] }}>
        <Typography variant="caption" color="text.secondary">
          Cumulative time error (min) — above 0 = prediction behind reality (too slow); below 0 = too fast. Elevation shaded.
        </Typography>
        <ResponsiveContainer width="100%" height={260}>
          <ComposedChart data={errorMap} margin={{ top: 22, right: 20, bottom: 0, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={colors.grey[800]} />
            <XAxis dataKey="distance_km" type="number" domain={[0, 'dataMax']} allowDecimals={false}
              tick={{ fontSize: 11, fill: colors.grey[400] }} unit=" km" />
            <YAxis yAxisId="err" tick={{ fontSize: 11, fill: colors.grey[400] }} />
            <YAxis yAxisId="alt" orientation="right" tick={{ fontSize: 11, fill: colors.grey[600] }} />
            <RTooltip
              contentStyle={{ backgroundColor: colors.primary[900], border: 'none', fontSize: 12 }}
              formatter={(v, name) => {
                if (name === 'Elevation') return [`${Math.round(v)} m`, name];
                return [`${Number(v).toFixed(2)} min`, name];
              }}
            />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <ReferenceLine yAxisId="err" y={0} stroke={colors.grey[500]} />
            <Area yAxisId="alt" dataKey="altitude" name="Elevation" fill={colors.grey[700]} stroke={colors.grey[700]} fillOpacity={0.3} />
            {(data.checkpoints || []).map((c, i) => (
              c.distance_km != null && (
                <ReferenceLine
                  key={i}
                  yAxisId="err"
                  x={c.distance_km}
                  stroke={colors.grey[600]}
                  strokeDasharray="2 4"
                  label={{
                    value: c.name,
                    angle: -90,
                    position: 'insideTop',
                    fill: colors.grey[300],
                    fontSize: 9,
                    offset: 10,
                  }}
                />
              )
            ))}
            <Line yAxisId="err" dataKey="elapsed_error_min" name="Cum. time error" stroke={colors.blueAccent[400]} dot={false} strokeWidth={2} />
          </ComposedChart>
        </ResponsiveContainer>

        <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
          GAP error (min/km) — above 0 = predicted GAP slower than actual.
        </Typography>
        <ResponsiveContainer width="100%" height={200}>
          <ComposedChart data={errorMap} margin={{ top: 10, right: 20, bottom: 0, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={colors.grey[800]} />
            <XAxis dataKey="distance_km" tick={{ fontSize: 11, fill: colors.grey[400] }} unit=" km" />
            <YAxis tick={{ fontSize: 11, fill: colors.grey[400] }} />
            <RTooltip contentStyle={{ backgroundColor: colors.primary[900], border: 'none', fontSize: 12 }}
              formatter={(v) => [`${Number(v).toFixed(2)} min/km`, 'GAP error']} />
            <ReferenceLine y={0} stroke={colors.grey[500]} />
            <Line dataKey="gap_error" name="GAP error" stroke={colors.redAccent[400]} dot={false} strokeWidth={1.5} />
          </ComposedChart>
        </ResponsiveContainer>
      </Paper>

      {/* Terrain breakdown — where the model is optimistic */}
      {(data.terrain_breakdown || []).length > 0 && (
        <>
          <SectionTitle>Error by terrain (GAP optimism)</SectionTitle>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            Pace error &lt; 0 = model runs the athlete too fast on that terrain (under-predicts time). Time impact = pace error × distance in band.
          </Typography>
          <TableContainer component={Paper} sx={{ mb: 3, backgroundColor: colors.primary[800] }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Gradient band</TableCell>
                  <TableCell align="right">Distance</TableCell>
                  <TableCell align="right">Pred GAP</TableCell>
                  <TableCell align="right">Actual GAP</TableCell>
                  <TableCell align="right">GAP err</TableCell>
                  <TableCell align="right">Pace err</TableCell>
                  <TableCell align="right">Time impact</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.terrain_breakdown.map((b) => (
                  <TableRow key={b.band}>
                    <TableCell>{BAND_LABEL[b.band] || b.band}</TableCell>
                    <TableCell align="right">{b.distance_km} km</TableCell>
                    <TableCell align="right">{fmtPace(b.mean_predicted_gap)}</TableCell>
                    <TableCell align="right">{fmtPace(b.mean_actual_gap)}</TableCell>
                    <TableCell align="right">{fmtSignedPace(b.mean_gap_error)}</TableCell>
                    <TableCell align="right" sx={{ color: b.mean_pace_error < 0 ? colors.redAccent[300] : colors.greenAccent[300] }}>
                      {fmtSignedPace(b.mean_pace_error)}
                    </TableCell>
                    <TableCell align="right" sx={{ color: b.est_time_impact_s < 0 ? colors.redAccent[300] : colors.greenAccent[300] }}>
                      {fmtSignedSecs(b.est_time_impact_s)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* Position breakdown — where drift accrues */}
      {(data.position_breakdown || []).length > 0 && (
        <>
          <SectionTitle>Error by race position</SectionTitle>
          <Paper sx={{ p: 2, mb: 3, backgroundColor: colors.primary[800] }}>
            <ResponsiveContainer width="100%" height={200}>
              <ComposedChart data={data.position_breakdown.map((p) => ({
                seg: `${p.from_km}`, drift_min: p.elapsed_drift_s != null ? p.elapsed_drift_s / 60 : null,
                gap_error: p.mean_gap_error,
              }))} margin={{ top: 10, right: 20, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={colors.grey[800]} />
                <XAxis dataKey="seg" tick={{ fontSize: 11, fill: colors.grey[400] }} unit=" km" />
                <YAxis yAxisId="d" tick={{ fontSize: 11, fill: colors.grey[400] }} />
                <RTooltip contentStyle={{ backgroundColor: colors.primary[900], border: 'none', fontSize: 12 }}
                  formatter={(v, n) => [n === 'Segment drift' ? `${Number(v).toFixed(1)} min` : `${Number(v).toFixed(2)} min/km`, n]} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <ReferenceLine yAxisId="d" y={0} stroke={colors.grey[500]} />
                <Bar yAxisId="d" dataKey="drift_min" name="Segment drift" fill={colors.blueAccent[500]} />
              </ComposedChart>
            </ResponsiveContainer>
            <Typography variant="caption" color="text.secondary">
              Time gained/lost by the prediction within each ~decile. Persistent negative bars late in the race indicate under-modeled fatigue.
            </Typography>
          </Paper>
        </>
      )}

      {/* Stop events — aid-station vs scattered */}
      {data.stop_events && (
        <>
          <SectionTitle>Stops: aid-station vs scattered</SectionTitle>
          <Paper sx={{ p: 2, mb: 3, backgroundColor: colors.primary[800] }}>
            <Box display="flex" gap={4} flexWrap="wrap" mb={1}>
              <Box>
                <Typography variant="caption" color="text.secondary">Aid-station stops</Typography>
                <Typography variant="h6">{fmtSecs(data.stop_events.aid_station_total_s)}</Typography>
                <Typography variant="caption" color="text.secondary">{data.stop_events.aid_station_count} events</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Scattered micro-stops</Typography>
                <Typography variant="h6" color={colors.redAccent[300]}>{fmtSecs(data.stop_events.scattered_total_s)}</Typography>
                <Typography variant="caption" color="text.secondary">{data.stop_events.scattered_count} events (not at aid)</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Total stop events</Typography>
                <Typography variant="h6">{data.stop_events.total_events}</Typography>
              </Box>
            </Box>
            {(data.stop_events.events || []).length > 0 && (
              <TableContainer sx={{ maxHeight: 260 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>km</TableCell>
                      <TableCell align="right">Duration</TableCell>
                      <TableCell align="center">At aid station?</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.stop_events.events.map((e, i) => (
                      <TableRow key={i}>
                        <TableCell>{e.distance_km}</TableCell>
                        <TableCell align="right">{fmtSecs(e.duration_s)}</TableCell>
                        <TableCell align="center">
                          <Chip label={e.at_aid_station ? 'aid' : 'scattered'} size="small"
                            color={e.at_aid_station ? 'default' : 'warning'}
                            sx={{ height: 18, fontSize: '0.65rem' }} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </>
      )}

      {/* Checkpoint splits */}
      {(data.checkpoints || []).length > 0 && (
        <>
          <SectionTitle>Checkpoint splits</SectionTitle>
          <TableContainer component={Paper} sx={{ mb: 3, backgroundColor: colors.primary[800] }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Checkpoint</TableCell>
                  <TableCell align="right">km</TableCell>
                  <TableCell align="right">Pred. arrival</TableCell>
                  <TableCell align="right">Actual arrival</TableCell>
                  <TableCell align="right">Cum. err</TableCell>
                  <TableCell align="right">Segment err</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.checkpoints.map((c, i) => (
                  <TableRow key={i}>
                    <TableCell>{c.name}</TableCell>
                    <TableCell align="right">{c.distance_km}</TableCell>
                    <TableCell align="right">{fmtSecs(c.predicted_elapsed_s)}</TableCell>
                    <TableCell align="right">{fmtSecs(c.actual_elapsed_s)}</TableCell>
                    <TableCell align="right" sx={{ color: c.cum_error_s < 0 ? colors.redAccent[300] : colors.greenAccent[300] }}>
                      {fmtSignedSecs(c.cum_error_s)}
                    </TableCell>
                    <TableCell align="right" sx={{ color: c.segment_error_s < 0 ? colors.redAccent[300] : colors.greenAccent[300] }}>
                      {fmtSignedSecs(c.segment_error_s)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* Climbs & descents */}
      {[...(data.climbs || []), ...(data.descents || [])].length > 0 && (
        <>
          <SectionTitle>Climbs &amp; descents</SectionTitle>
          <TableContainer component={Paper} sx={{ mb: 3, backgroundColor: colors.primary[800] }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Type</TableCell>
                  <TableCell align="right">Range</TableCell>
                  <TableCell align="right">Grade</TableCell>
                  <TableCell align="right">Pred. time</TableCell>
                  <TableCell align="right">Actual time</TableCell>
                  <TableCell align="right">Time err</TableCell>
                  <TableCell align="right">GAP err</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[...(data.climbs || []), ...(data.descents || [])].map((c, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <Chip label={c.kind} size="small"
                        color={c.kind === 'climb' ? 'warning' : 'info'}
                        sx={{ height: 18, fontSize: '0.65rem' }} />
                    </TableCell>
                    <TableCell align="right">{c.start_km}–{c.end_km} km</TableCell>
                    <TableCell align="right">{c.avg_gradient_pct}%</TableCell>
                    <TableCell align="right">{fmtSecs(c.predicted_duration_s)}</TableCell>
                    <TableCell align="right">{c.matched ? fmtSecs(c.actual_duration_s) : '—'}</TableCell>
                    <TableCell align="right" sx={{ color: c.duration_error_s < 0 ? colors.redAccent[300] : colors.greenAccent[300] }}>
                      {fmtSignedSecs(c.duration_error_s)}
                    </TableCell>
                    <TableCell align="right">{fmtSignedPace(c.gap_error)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* Re-run diff */}
      {rerun && (
        <>
          <Divider sx={{ my: 3 }} />
          <SectionTitle>Current-algorithm re-run</SectionTitle>
          <Paper sx={{ p: 2, mb: 2, backgroundColor: colors.primary[800] }}>
            <Box display="flex" gap={4} flexWrap="wrap" mb={1}>
              <Box>
                <Typography variant="caption" color="text.secondary">Fresh predicted finish</Typography>
                <Typography variant="h6">{fmtSecs(rerun.algorithm_drift?.fresh_total_time_s)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Stored predicted finish</Typography>
                <Typography variant="h6">{fmtSecs(rerun.algorithm_drift?.stored_total_time_s)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Algorithm drift</Typography>
                <Typography variant="h6" color={colors.blueAccent[300]}>
                  {fmtSignedSecs(rerun.algorithm_drift?.drift_s)}{' '}
                  {rerun.algorithm_drift?.drift_pct != null ? `(${rerun.algorithm_drift.drift_pct}%)` : ''}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Fresh vs actual error</Typography>
                <Typography variant="h6" color={colors.greenAccent[300]}>
                  {fmtSignedSecs(rerun.fresh_report?.overall?.total_time_error_s)}
                </Typography>
              </Box>
            </Box>
            <Typography variant="caption" color="text.secondary">
              Drift = how much the current algorithm's output differs from the stored prediction for the exact same inputs.
              Fresh vs actual = how the current algorithm would grade against this race.
            </Typography>
          </Paper>
        </>
      )}
    </Box>
  );
}

function SectionTitle({ children }) {
  return <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>{children}</Typography>;
}
